import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  alpha,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import EmailIcon from "@mui/icons-material/Email";

import PageContainer from "../layouts/PageContainer";
import AppCircularProgress from "../components/common/AppCircularProgress";
import UpdateBranchSettingsModal from "../components/settings/UpdateBranchSettingsModal";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import AppEmailModal from "../components/common/AppEmailModal";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { usePermissions } from "../hooks/usePermissions";
import {
  getBranchSettings,
  resetSettingsServerStatus,
  clearAppCache,
} from "../redux/actions/settingsActions";
import { emailBranchDetails } from "../redux/actions/branchActions";

const SettingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const { userInfo } = useAppSelector((state) => state.auth);
  const {
    branchSettings,
    loadingBranchSettings,
    loadingUpdateBranchSettings,
    errorUpdateBranchSettings,
    serverStatus,
  } = useAppSelector((state) => state.settings);
  const { loadingEmail } = useAppSelector((state) => state.branch);

  const [clearCacheOpen, setClearCacheOpen] = useState(false);
  const [branchModalOpen, setBranchModalOpen] = useState<boolean>(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const canUpdate = hasPermission("branch_setting:update");

  useEffect(() => {
    dispatch(getBranchSettings());
  }, [dispatch]);

  useEffect(() => {
    if (
      serverStatus?.operation === "update-branch-settings" &&
      serverStatus?.status === 200
    ) {
      dispatch(resetSettingsServerStatus());
      setBranchModalOpen(false);
    }
  }, [serverStatus, dispatch]);

  return (
    <PageContainer
      title="Settings & Configurations"
      subtitle="Manage regional parameters and external payment & communication integrations."
      breadcrumbs={[
        { label: "Dashboard", to: "/dashboard" },
        { label: "Settings", to: undefined },
      ]}
    >
      <Stack spacing={4} sx={{ maxWidth: 800, mx: "auto", pb: 4 }}>
        {/* 1. PROFILE OVERVIEW */}
        <Box>
          <Typography
            variant="overline"
            sx={{ px: 1, color: "text.secondary", fontWeight: 700 }}
          >
            Signed-In Account
          </Typography>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 3,
                "&:last-child": { pb: 3 },
              }}
            >
              <PersonIcon color="primary" sx={{ fontSize: 36 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {userInfo?.firstName} {userInfo?.lastName}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Role: {userInfo?.userRole?.replace(/_/g, " ")} •{" "}
                  {userInfo?.email}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* 2. BRANCH INTEGRATIONS & SETTINGS */}
        <Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              px: 1,
            }}
          >
            <Typography
              variant="overline"
              sx={{ fontWeight: 700, color: "text.secondary" }}
            >
              Branch Server Parameters
            </Typography>
            {canUpdate && (
              <Stack direction="row" spacing={1}>
                <AppButton
                  size="small"
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => setEmailModalOpen(true)}
                >
                  Email Config
                </AppButton>
                <AppButton
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<EditIcon />}
                  onClick={() => setBranchModalOpen(true)}
                >
                  Edit Settings
                </AppButton>
              </Stack>
            )}
          </Stack>

          {loadingBranchSettings && !branchSettings ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <AppCircularProgress />
            </Box>
          ) : (
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            >
              <List disablePadding>
                <ListItem>
                  <ListItemText
                    primary="Timezone"
                    secondary={branchSettings?.timezone || "Not Set"}
                  />
                </ListItem>
                <Divider />

                <ListItem>
                  <ListItemText
                    primary="Payments Gateway Integration"
                    secondary={
                      branchSettings?.paymentIntegration?.active
                        ? "Active (Online)"
                        : "Disabled"
                    }
                    slotProps={{
                      secondary: {
                        sx: {
                          color: branchSettings?.paymentIntegration?.active
                            ? "success.main"
                            : "text.secondary",
                          fontWeight: 600,
                        },
                      },
                    }}
                  />
                </ListItem>
                <Divider />

                <ListItem>
                  <ListItemText
                    primary="Communications Gateway Hub"
                    secondary={
                      branchSettings?.communicationIntegration?.active
                        ? "Active (Online)"
                        : "Disabled"
                    }
                    slotProps={{
                      secondary: {
                        sx: {
                          color: branchSettings?.communicationIntegration
                            ?.active
                            ? "success.main"
                            : "text.secondary",
                          fontWeight: 600,
                        },
                      },
                    }}
                  />
                </ListItem>
              </List>
            </Card>
          )}
        </Box>

        {/* 3. ADVANCED / CLEAR CACHE */}
        <Box>
          <Typography
            variant="overline"
            sx={{ px: 1, color: "error.main", fontWeight: 700 }}
          >
            Advanced Diagnostics
          </Typography>
          <Stack
            divider={<Divider />}
            sx={{
              border: "1px solid",
              borderColor: "error.light",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.05),
            }}
          >
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Clear Local Cache
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Wipes cached data and logs you out.
                </Typography>
              </Box>
              <AppButton
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={() => setClearCacheOpen(true)}
              >
                Clear & Logout
              </AppButton>
            </Box>
          </Stack>
        </Box>
      </Stack>

      {/* MODAL */}
      {branchModalOpen && (
        <UpdateBranchSettingsModal
          open={branchModalOpen}
          onClose={() => setBranchModalOpen(false)}
          branchSettings={branchSettings}
          loading={loadingUpdateBranchSettings}
          error={errorUpdateBranchSettings}
        />
      )}
      <ConfirmDialog
        open={clearCacheOpen}
        onClose={() => setClearCacheOpen(false)}
        onConfirm={() => dispatch(clearAppCache())}
        title="Clear App Cache?"
        description="This will clear your locally saved session data, and sign you out immediately."
        confirmText="Yes, Clear Everything"
      />
      <AppEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSubmit={(email) => dispatch(emailBranchDetails({ email }))}
        loading={loadingEmail}
        title="Email Configuration"
        description="Send the current branch config."
      />
    </PageContainer>
  );
};

export default SettingsPage;
